/**
 * Integration Marketplace Demo — pure ingest helpers (no store dependency).
 *
 * THROWAWAY DEMO CODE (see ./README.md). Kept free of any store import so both
 * the seed and the runtime actions can use them without a circular dependency.
 *
 * The item master is ONE combined list. Ingesting a products link matches each
 * source product to the catalog by SKU (sanity-checking vendor + price) and
 * either attaches a new raw source link to the existing item or inserts a new
 * item. Every catalog item carries all the raw source links that resolve to it.
 */

import { nowIso } from './time';
import type {
  CatalogItem,
  ConnectedLink,
  ConnectedSource,
  ItemSourceRef,
  ResolvedSourceProduct,
  SanityCheck,
} from './types';

/** A fixed "now" for deterministic demo timestamps (connectedAt/createdAt). */
export const NOW_ISO = '2026-06-23T15:30:00Z';

export function toConnectedLink(
  link: ConnectedLink | Omit<ConnectedLink, 'status' | 'lastSyncedAt'>,
  status: ConnectedLink['status'] = 'synced',
  syncedAt: string = nowIso(),
): ConnectedLink {
  return { ...link, status, lastSyncedAt: syncedAt };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function normalizeVendor(v: string): string {
  return v.trim().toLowerCase();
}

/** Sanity-check a source product against the catalog item it matched on SKU. */
export function sanityCheck(
  source: ResolvedSourceProduct,
  existing: CatalogItem,
): SanityCheck {
  const priceDelta = round2(source.listPrice - existing.listPrice);
  const vendorMatch =
    normalizeVendor(source.manufacturer) ===
    normalizeVendor(existing.manufacturer);
  let confidence = 1;
  if (Math.abs(priceDelta) > 0.001) confidence -= 0.08;
  if (!vendorMatch) confidence -= 0.18;
  return {
    priceDelta,
    vendorMatch,
    confidence: Math.max(0, round2(confidence)),
  };
}

function sourceRef(
  source: Pick<ConnectedSource, 'id' | 'name' | 'integrationId'>,
  link: Pick<ConnectedLink, 'id' | 'sourceName' | 'lastSyncedAt'>,
  rawRecord: ItemSourceRef['rawRecord'],
  sanity?: SanityCheck,
): ItemSourceRef {
  return {
    sourceId: source.id,
    sourceName: source.name,
    integrationId: source.integrationId,
    sourceLinkId: link.id,
    sourceTable: link.sourceName,
    rawRecord,
    lastSyncedAt: link.lastSyncedAt ?? nowIso(),
    sanity,
  };
}

function newItem(
  product: ResolvedSourceProduct,
  origin: CatalogItem['origin'],
  ref: ItemSourceRef,
): CatalogItem {
  return {
    id: `item-${product.sku}`,
    sku: product.sku,
    name: product.name,
    description: product.description,
    category: product.category,
    manufacturer: product.manufacturer,
    uom: product.uom,
    listPrice: product.listPrice,
    cost: product.cost,
    onHand: product.onHand,
    origin,
    sources: [ref],
  };
}

/**
 * Build the baseline (existing) catalog from a source's products link. The
 * source is treated as the system of record, so each item's single ref carries
 * no sanity check.
 */
export function buildBaselineCatalog(
  source: Pick<ConnectedSource, 'id' | 'name' | 'integrationId'>,
  link: ConnectedLink,
): CatalogItem[] {
  const products = link.products ?? [];
  return products.map((p, i) =>
    newItem(p, 'existing', sourceRef(source, link, link.sampleRows[i] ?? {})),
  );
}

/**
 * Merge a products link into the combined catalog (mutating it in place):
 * match each source product to an existing item by SKU (attaching a sanity-
 * checked raw link) or insert it as a new item. Returns the match/insert tally.
 */
export function mergeProductsIntoCatalog(
  catalog: CatalogItem[],
  source: Pick<ConnectedSource, 'id' | 'name' | 'integrationId'>,
  link: ConnectedLink,
): { matched: number; inserted: number } {
  const products = link.products ?? [];
  let matched = 0;
  let inserted = 0;

  products.forEach((product, i) => {
    const raw = link.sampleRows[i] ?? {};
    const existing = catalog.find((c) => c.sku === product.sku);
    if (existing) {
      const ref = sourceRef(source, link, raw, sanityCheck(product, existing));
      // Idempotent: replace any prior ref from the same source.
      existing.sources = existing.sources.filter(
        (s) => s.sourceId !== source.id,
      );
      existing.sources.push(ref);
      matched += 1;
    } else {
      catalog.push(newItem(product, 'imported', sourceRef(source, link, raw)));
      inserted += 1;
    }
  });

  return { matched, inserted };
}

/** Derived display status for a catalog item. */
export function itemStatus(item: CatalogItem): 'existing' | 'matched' | 'new' {
  if (item.origin === 'imported') return 'new';
  return item.sources.length > 1 ? 'matched' : 'existing';
}

/** True when any matched source ref disagrees with the catalog on a field. */
export function hasDiscrepancy(item: CatalogItem): boolean {
  return item.sources.some(
    (s) =>
      s.sanity &&
      (Math.abs(s.sanity.priceDelta) > 0.001 || !s.sanity.vendorMatch),
  );
}
